import { useFacilityContext } from '@aspendental/shared-components-web';
import { BinaryChoice, BinaryChoiceSchema, DateTextInput, Form, MaskedPhoneNumberInput, MaskedPhoneNumberInputSchema, SchemaBuilder, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import { Alert, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import NeedsBasedQuestions from '@src/components/NeedsBasedQuestions';
import { REASON_FOR_VISIT_ID } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { AppointmentErrorMessages, DataLayerStepNames } from '@src/constants';
import { useLanguagePreferenceTracking } from '@src/hooks/useLanguagePreferenceTracking';
import { useScrollToFirstError } from '@src/hooks/useScrollToFirstError';
import { DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import { EventAdditionalPatientInformationValueEventTypeEnum } from '@src/lib/__generated/user-events-api';
import { schedulerLeadEvent } from '@src/services/schedulerLeadEvent';
import { userEvent } from '@src/services/userEvent';
import { PatientInformationSchema, useAppStore, useAppointmentStore, useFormSectionStore, useGlobalUXStore, useGuarantorInformationFormStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import useNeedsBasedQuestionsStore from '@src/store/modules/needs-based-questions.store';
import { ageEligibleForProcedure, guarantorInformationNeeded } from '@src/utils/business-logic';
import { fireDataLayerEvent } from '@src/utils/data-layer-utils';
import { dateInvalid } from '@src/utils/date-parser';
import { formatPhoneNumber } from '@src/utils/format-phone-number';
import { trackFormValidationError } from '@src/utils/form-analytics';
import { unacceptableInputs } from '@src/utils/offensive-words';
import { sessionStorageHelper } from '@src/utils/session-storage-utils';
function isNBQFormValid(data: Record<string, string[]> | undefined) {
  for (const value of Object.values(data ?? {})) {
    if (value.length === 0) return false;
  }
  return true;
}

/**
 * Component: ConsolidatedPatientInformationForm
 *
 * Used exclusively in the V2 A/B test flow. Combines the fields from
 * PatientInformationForm and AdditionalPatientInformationForm into a single
 * tab, preserving full GTM tracking parity by firing both the
 * `patient_information` event on mount and the `additional_patient_information`
 * event on submit.
 */
const ConsolidatedPatientInformationForm = ({
  formId,
  shouldUseMottoConfig = false
}: {
  formId: string;
  shouldUseMottoConfig?: boolean;
}) => {
  const completeActiveNode = useFormSectionStore(store => store.completeActiveNode);
  const setGuarantorInfoNeeded = useFormSectionStore(store => store.setGuarantorInfoNeeded);
  const setGuarantorRequired = useGuarantorInformationFormStore(store => store.setRequired);
  const patientAge = usePatientInformationFormStore(store => store.patientAge);
  const {
    mottoFlow
  } = useAppStore(store => store);
  const {
    reasonForVisit
  } = useReasonForVisitStore(store => store);
  const {
    appointmentFacility,
    appointmentSlot
  } = useAppointmentStore(store => store);
  const webFacilityDetails = useFacilityContext();
  const openDialogWithError = useGlobalUXStore(store => store.openDialogWithError);
  const {
    t
  } = useTranslation(['patientInformationForm', 'additionalPatientInformation']);
  const router = useRouter();
  const {
    locale
  } = router;
  const {
    email,
    setEmail,
    phonenumber,
    setPhonenumber,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dateOfBirth,
    setDateOfBirth,
    hasInsurance,
    setHasInsurance
  } = usePatientInformationFormStore(store => store);
  const {
    hasNeedsBasedQuestions,
    needsBasedAnswers,
    setNeedsBasedAnswers
  } = useNeedsBasedQuestionsStore(store => store);
  const [hasError, setHasError] = useState(false);
  const [offensiveInputsFound, setOffensiveInputsFound] = useState(false);
  const initialValueDateOfBirth = dateInvalid(dateOfBirth) ? '' : format(dateOfBirth as Date, 'MM/dd/yyyy');
  const defaultValues = {
    firstName,
    lastName,
    dateOfBirth: initialValueDateOfBirth,
    email,
    phonenumber: formatPhoneNumber(phonenumber) || phonenumber,
    hasInsurance
  };
  const schema = SchemaBuilder([{
    name: 'firstName',
    validation: PatientInformationSchema.firstName
  }, {
    name: 'lastName',
    validation: PatientInformationSchema.lastName
  }, {
    name: 'dateOfBirth',
    validation: PatientInformationSchema.dateOfBirth
  }, {
    name: 'email',
    validation: PatientInformationSchema.email
  }, {
    name: 'phonenumber',
    validation: MaskedPhoneNumberInputSchema.options.default
  }, {
    name: 'hasInsurance',
    validation: BinaryChoiceSchema.options.default
  }]);

  // Reset both deduplication keys on mount so tracking fires even if the
  // user navigates backwards and re-enters this tab.
  sessionStorageHelper.setKey(DataLayerStepNames.patientInformation, 'false');
  sessionStorageHelper.setKey(DataLayerStepNames.additionalPatientInfo, 'false');
  useEffect(() => {
    // Fire patient_information step event on mount, matching PatientInformationForm behaviour.
    fireDataLayerEvent(DataLayerStepNames.patientInformation, {
      language_preference: locale,
      brand: mottoFlow ? 'mottoaligners' : 'aspendental',
      step_name: DataLayerStepNames.patientInformation,
      reason_for_visit: reasonForVisit,
      reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
      ...(webFacilityDetails?.officeInfo?.code && {
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code
      }),
      ...(webFacilityDetails?.officeInfo?.name && {
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      })
    });
  }, [reasonForVisit, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, mottoFlow]);

  // Language-toggle tracking for this step. Using patientInformation as the
  // primary step name since this tab replaces that step in the V2 flow.
  useLanguagePreferenceTracking({
    stepName: DataLayerStepNames.patientInformation,
    webFacilityDetails,
    brand: mottoFlow ? 'mottoaligners' : 'aspendental'
  });
  const handleSubmit = (fields: FieldValues) => {
    if (hasNeedsBasedQuestions && !isNBQFormValid(needsBasedAnswers)) {
      setHasError(true);
      return;
    } else {
      setHasError(false);
    }
    const unacceptableFields = unacceptableInputs([{
      name: 'firstName',
      value: fields.firstName
    }, {
      name: 'lastName',
      value: fields.lastName
    }]);
    if (unacceptableFields.length) {
      setOffensiveInputsFound(true);
      trackFormValidationError({
        formName: 'patient_information',
        errorType: 'offensive_input',
        fields: unacceptableFields
      });
      return;
    }
    setOffensiveInputsFound(false);
    setFirstName(fields.firstName);
    setLastName(fields.lastName);
    setDateOfBirth(fields.dateOfBirth);
    setEmail(fields.email);
    setPhonenumber(fields.phonenumber.replaceAll(/[^\d]/g, ''));
    setHasInsurance(fields.hasInsurance);
    const age = patientAge() || 0;
    const ageOK = ageEligibleForProcedure(age, reasonForVisit as DigitalVisitReasonEnum);
    if (!ageOK) {
      openDialogWithError(AppointmentErrorMessages.onlineUnavailable);
      return;
    }
    const guarantorRequired = guarantorInformationNeeded(age);
    setGuarantorRequired(guarantorRequired);
    setGuarantorInfoNeeded(guarantorRequired);
    void schedulerLeadEvent({
      firstName: fields.firstName,
      lastName: fields.lastName,
      email: fields.email,
      dateOfBirth: fields.dateOfBirth,
      reasonForVisit,
      shouldUseMottoConfig
    });
    setTimeout(() => {
      completeActiveNode();
    }, 50);

    // Fire additional_patient_information step event on submit, matching
    // AdditionalPatientInformationForm behaviour. Lead score fields are
    // available at this point because Date & Time has been completed in a
    // prior session (session slot restore) or will be next — the event
    // shape is kept identical to the original for GTM schema parity.
    fireDataLayerEvent(DataLayerStepNames.additionalPatientInfo, {
      language_preference: locale,
      brand: mottoFlow ? 'mottoaligners' : 'aspendental',
      lead_score: appointmentSlot?.patientValue,
      lead_tier_national: appointmentSlot?.nationalPatientTier,
      lead_tier_office: appointmentSlot?.patientTier,
      step_name: DataLayerStepNames.additionalPatientInfo,
      reason_for_visit: reasonForVisit,
      reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
      ...(webFacilityDetails?.officeInfo?.code && {
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code
      }),
      ...(webFacilityDetails?.officeInfo?.name && {
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      }),
      ...(appointmentFacility?.facilityCode && {
        appointment_facility_id: appointmentFacility?.facilityCode
      }),
      ...(appointmentFacility?.title && {
        appointment_facility_name: appointmentFacility?.title
      })
    });
    userEvent({
      event_type: EventAdditionalPatientInformationValueEventTypeEnum.AdditionalPatientInfo,
      event_data: {
        facility_code: appointmentFacility?.facilityCode ?? '',
        first_name: fields.firstName,
        last_name: fields.lastName,
        date_of_birth: new Intl.DateTimeFormat('en-US', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(fields.dateOfBirth ?? undefined),
        email_address: fields.email,
        mobile_phone: fields.phonenumber,
        is_insured: fields.hasInsurance
      }
    });
  };
  const form = useHookForm({
    schema,
    defaultValues,
    shouldFocusError: false
  });
  useScrollToFirstError(form as any, formId);
  return <Form form={form as any} onSubmit={handleSubmit} formId={formId} data-sentry-element="Form" data-sentry-component="ConsolidatedPatientInformationForm" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx">
			<Grid container spacing={2} data-sentry-element="Grid" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx">
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx">
					<TextInput name="firstName" label={t('firstNameLabel')} data-test-id="patient-info-firstname" data-private data-sentry-element="TextInput" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx">
					<TextInput name="lastName" label={t('lastNameLabel')} data-test-id="patient-info-lastname" data-private data-sentry-element="TextInput" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx" />
				</Grid>
				{offensiveInputsFound ? <Grid item xs={12}>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('offensiveInputWarning')}</Typography>
						</Alert>
					</Grid> : null}
				<Grid item xs={12} sm={6} data-private data-sentry-element="Grid" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx">
					<DateTextInput label={t('dobLabel')} name="dateOfBirth" autoComplete="bday" required data-sentry-element="DateTextInput" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx" />
				</Grid>
				<Grid item sm={6} sx={{
        display: {
          xs: 'none',
          sm: 'block'
        }
      }} data-sentry-element="Grid" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx" />
				<Grid item xs={12} sm={6} data-private data-sentry-element="Grid" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx">
					<TextInput name="email" label={t('additionalPatientInformation:emailLabel')} enforceAlphaNumeric={false} placeholder="email@domain.com" data-sentry-element="TextInput" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx">
					<MaskedPhoneNumberInput name="phonenumber" label={t('additionalPatientInformation:phoneNumLabel')} data-private data-sentry-element="MaskedPhoneNumberInput" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={7} data-sentry-element="Grid" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx">
					<BinaryChoice groupLabel={t('additionalPatientInformation:hasInsurance')} name="hasInsurance" defaultSelection={defaultValues.hasInsurance} variant="bodyLargeSemiBold" color="text.primary" data-sentry-element="BinaryChoice" data-sentry-source-file="ConsolidatedPatientInformationForm.tsx" />
				</Grid>
				{hasNeedsBasedQuestions ? <NeedsBasedQuestions setNeedsBasedAnswers={setNeedsBasedAnswers} needsBasedAnswers={needsBasedAnswers} hasError={hasError} /> : null}
			</Grid>
		</Form>;
};
export default ConsolidatedPatientInformationForm;